import SectionHeader from "@/components/SectionHeader";
import { euro, euroDezimal } from "@/utils/preisAnalyse";

/**
 * Der Teil der Angebotsseite, der nicht Buchungsstrecke ist.
 *
 * Die 52 Angebotsseiten hatten im Schnitt 167 Wörter: Überschrift, Pakete,
 * Warenkorb. Dabei liegen zu jedem Angebot Daten in der Datenbank, die sonst
 * niemand veröffentlicht — die Anfahrtsstaffel in Kilometern und die
 * Stornostaffel in Tagen. Genau danach wird gefragt, bevor jemand anfragt.
 *
 * Alles hier entsteht aus diesen Feldern. Fehlt eine Angabe, fehlt der
 * Abschnitt — geraten wird nichts.
 */

function Abschnitt({ id, titel, untertitel, children }) {
  return (
    <SectionHeader id={id} title={titel} subtitle={untertitel}>
      <div className="mx-auto mt-6 max-w-3xl text-left text-[15px] leading-7 text-slate-700">
        {children}
      </div>
    </SectionHeader>
  );
}

const KAMERA_BEDEUTUNG = {
  Spiegelreflexkamera:
    "Die beste Wahl für abends: Eine Spiegelreflexkamera liefert auch bei wenig Licht scharfe Bilder.",
  "Digitale Systemkamera (DSLM)":
    "Eine Systemkamera arbeitet wie eine Spiegelreflex, nur kompakter — auch bei wenig Licht scharf.",
  "4K-Kamera": "Eine 4K-Kamera liefert hohe Auflösung, ist bei wenig Licht aber empfindlicher als eine Spiegelreflex.",
  "HD-Kamera":
    "Eine HD-Kamera reicht bei guter Beleuchtung. In dunklen Räumen sieht man den Unterschied zu einer Spiegelreflex.",
};

const LICHT_BEDEUTUNG = {
  "Studio-Blitz":
    "Ein Studio-Blitz friert Bewegung ein — wichtig, sobald vor der Box getanzt wird.",
  "Foto-Blitz": "Ein Blitz friert Bewegung ein, auch wenn es dunkel wird.",
  "LED-Blitz": "Der LED-Blitz hilft bei wenig Licht und bleibt dabei leise.",
  "LED-Dauerlicht":
    "Dauerlicht ist angenehm für Videos, kommt bei dunklen Räumen und Bewegung aber schneller an seine Grenze.",
};

export default function AngebotDetails({ angebot }) {
  if (!angebot) return null;

  const { booth, packages, delivery, cancellation, printLayouts } = angebot;
  const lieferung = delivery?.selfDelivery;
  const versand = delivery?.post;
  const abholort = delivery?.pickup;
  const staffel = lieferung?.ranges?.filter((r) => r && r.end) || [];
  const storno = cancellation?.refundRanges?.filter((r) => r) || [];
  const grundpreis = booth?.price || 0;

  return (
    <div className="space-y-10 pt-12 lg:space-y-14 lg:pt-16">
      {booth && (
        <Abschnitt
          id="ausstattung"
          titel={`Was die ${booth.name || "Fotobox"} mitbringt`}
          untertitel="Die Ausstattung im Einzelnen — und was sie am Abend bedeutet."
        >
          <dl className="space-y-4">
            {booth.camType && (
              <div className="border-l-2 border-slate-200 pl-5">
                <dt className="font-semibold text-slate-900">
                  Kamera: {booth.camType}
                </dt>
                <dd className="mt-1 text-sm leading-6 text-slate-600">
                  {KAMERA_BEDEUTUNG[booth.camType] ||
                    "Die Kamera bestimmt, wie die Bilder bei wenig Licht aussehen."}
                </dd>
              </div>
            )}
            {booth.lightType && (
              <div className="border-l-2 border-slate-200 pl-5">
                <dt className="font-semibold text-slate-900">
                  Licht: {booth.lightType}
                </dt>
                <dd className="mt-1 text-sm leading-6 text-slate-600">
                  {LICHT_BEDEUTUNG[booth.lightType] ||
                    "Die Beleuchtung entscheidet, ob Bewegung verwackelt."}
                </dd>
              </div>
            )}
            {(booth.controls?.touch || booth.controls?.liveView) && (
              <div className="border-l-2 border-slate-200 pl-5">
                <dt className="font-semibold text-slate-900">
                  Bedienung
                  {booth.screenSize ? ` über ${booth.screenSize}-Zoll-Bildschirm` : ""}
                </dt>
                <dd className="mt-1 text-sm leading-6 text-slate-600">
                  {booth.controls?.liveView
                    ? "Mit Live-Vorschau: Wer sich vor dem Auslösen sieht, versucht es nicht dreimal."
                    : "Die Gäste bedienen die Box selbst."}
                  {booth.controls?.touch ? " Bedient wird per Touchscreen." : ""}
                </dd>
              </div>
            )}
            {(booth.digital?.gallery || booth.digital?.usb || booth.digital?.phone) && (
              <div className="border-l-2 border-slate-200 pl-5">
                <dt className="font-semibold text-slate-900">
                  Die Bilder nach der Feier
                </dt>
                <dd className="mt-1 text-sm leading-6 text-slate-600">
                  {[
                    booth.digital?.gallery && "Online-Galerie",
                    booth.digital?.phone && "direkt aufs Handy",
                    booth.digital?.usb && "auf USB-Stick",
                  ]
                    .filter(Boolean)
                    .join(", ")}
                  . Der Ausdruck bleibt beim Gast — so habt ihr die Bilder trotzdem.
                </dd>
              </div>
            )}
          </dl>
        </Abschnitt>
      )}

      {packages?.length === 1 && (
        <Abschnitt
          id="paket"
          titel="Was im Paket enthalten ist"
          untertitel="Dieser Anbieter bietet ein Paket an — hier steht, was es umfasst."
        >
          <p>
            <strong className="font-semibold text-slate-900">
              {packages[0].name}
            </strong>{" "}
            kostet {euro(grundpreis + (packages[0].price || 0))} €.{" "}
            {packages[0].printFlatrate
              ? "Gedruckt wird ohne Begrenzung — ihr müsst nicht rechnen, wie viele Bilder noch übrig sind."
              : packages[0].printer?.paperStorageSize
              ? `Enthalten ist Papier für ${packages[0].printer.paperStorageSize} Ausdrucke — bei zwei bis drei Bildern pro Gast reicht das für etwa ${Math.floor(
                  packages[0].printer.paperStorageSize / 2.5
                )} Gäste.`
              : "Die Bilder gibt es digital — gedruckt wird nicht."}
          </p>
        </Abschnitt>
      )}

      {packages?.length > 1 && (
        <Abschnitt
          id="pakete"
          titel="Die Pakete im Vergleich"
          untertitel="Was sich zwischen den Paketen tatsächlich unterscheidet."
        >
          <div className="overflow-x-auto">
            <table className="w-full min-w-[420px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                  <th scope="col" className="py-2 pr-4 font-semibold">Paket</th>
                  <th scope="col" className="py-2 pr-4 font-semibold">Druck</th>
                  <th scope="col" className="py-2 text-right font-semibold">Gesamt</th>
                </tr>
              </thead>
              <tbody>
                {packages.map((p) => (
                  <tr key={p.id || p.name} className="border-b border-slate-100 last:border-0">
                    <th scope="row" className="py-2.5 pr-4 font-medium text-slate-900">
                      {p.name}
                    </th>
                    <td className="py-2.5 pr-4 text-slate-600">
                      {p.printFlatrate
                        ? "unbegrenzt"
                        : p.printer?.paperStorageSize
                        ? `Papier für ${p.printer.paperStorageSize} Bilder`
                        : "ohne Drucker"}
                    </td>
                    <td className="py-2.5 text-right font-semibold tabular-nums text-slate-900">
                      {euro(grundpreis + (p.price || 0))} €
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4">
            Die Angabe ist die Papiermenge, die im Paket steckt — nicht eine
            Grenze des Geräts. Für den Abend ist sie trotzdem die praktische
            Grenze, denn Nachschub bekommt ihr mitten in der Feier nicht mehr.
          </p>
          <p className="mt-3">
            Als Faustregel gilt: zwei bis drei Ausdrucke pro Gast. Bei 80
            Gästen sind das rund 200 Bilder — bei einem Fotobox-Gästebuch das
            Doppelte, weil dann jedes Motiv zweimal gedruckt wird.
          </p>
        </Abschnitt>
      )}

      {versand && (
        <Abschnitt
          id="versand"
          titel="Wann die Box ankommt"
          untertitel="Per Paket — ohne Termin und ohne Übergabe."
        >
          <p>
            Die Box kommt{" "}
            {versand.daysBeforeStart === versand.daysBeforeEnd
              ? `${versand.daysBeforeStart} ${
                  versand.daysBeforeStart === 1 ? "Tag" : "Tage"
                }`
              : `${versand.daysBeforeStart} bis ${versand.daysBeforeEnd} Tage`}{" "}
            vor eurem Termin an die angegebene Adresse.{" "}
            {versand.costs === 0
              ? "Hin- und Rückversand sind im Preis enthalten."
              : `Für den Versand kommen ${euro(versand.costs)} € dazu.`}
          </p>
          <p className="mt-3">
            Aufgebaut ist sie in einer Viertelstunde: Stativ ausklappen,
            einstecken, fertig. Nach der Feier geht sie im selben Karton
            zurück — das Rücksendeetikett liegt bei.
          </p>
        </Abschnitt>
      )}

      {abholort?.city && !lieferung?.city && (
        <Abschnitt
          id="abholung"
          titel={`Abholung in ${abholort.city}`}
          untertitel="Selbst holen statt liefern lassen."
        >
          <p>
            Die Box könnt ihr in {abholort.city}
            {abholort.zip ? ` (${abholort.zip})` : ""} abholen. Das spart die
            Anfahrt — ihr braucht dafür ein Auto mit ausreichend Platz und
            solltet vorher klären, wann die Übergabe passt.
          </p>
        </Abschnitt>
      )}

      {lieferung?.city && (
        <Abschnitt
          id="lieferung"
          titel={`Liefergebiet ab ${lieferung.city}`}
          untertitel="Wie weit geliefert wird — und ab wann es etwas kostet."
        >
          {staffel.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[360px] border-collapse text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                      <th scope="col" className="py-2 pr-4 font-semibold">Entfernung</th>
                      <th scope="col" className="py-2 text-right font-semibold">Anfahrt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staffel.map((r) => (
                      <tr key={`${r.start}-${r.end}`} className="border-b border-slate-100 last:border-0">
                        <th scope="row" className="py-2.5 pr-4 font-medium text-slate-900">
                          {r.start} bis {r.end} km
                        </th>
                        <td className="py-2.5 text-right tabular-nums text-slate-600">
                          {r.pricePerKm
                            ? `${euroDezimal(r.pricePerKm)} € je km`
                            : "inklusive"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-4">
                Gerechnet wird ab {lieferung.city}
                {lieferung.zip ? ` (${lieferung.zip})` : ""}. Weiter als{" "}
                {Math.max(...staffel.map((r) => r.end))} km liefert dieser
                Anbieter nicht — für alles darüber findest du im Vergleich
                Anbieter, die näher sitzen.
              </p>
            </>
          ) : (
            <p>
              Geliefert wird ab {lieferung.city}
              {lieferung.zip ? ` (${lieferung.zip})` : ""}.
            </p>
          )}
          {typeof lieferung.daysBeforeEvent === "number" && (
            <p className="mt-3">
              Aufgebaut wird{" "}
              {lieferung.daysBeforeEvent === 0
                ? "am Tag der Veranstaltung"
                : `${lieferung.daysBeforeEvent} ${
                    lieferung.daysBeforeEvent === 1 ? "Tag" : "Tage"
                  } vorher`}
              .
            </p>
          )}
        </Abschnitt>
      )}

      {storno.length === 0 && cancellation?.freeReschedule && (
        <Abschnitt
          id="umbuchung"
          titel="Wenn sich der Termin verschiebt"
          untertitel="Was gilt, wenn aus dem Datum nichts wird."
        >
          <p>
            Eine Umbuchung auf einen anderen Termin ist bis{" "}
            {cancellation.freeRescheduleDays || 0}{" "}
            {cancellation.freeRescheduleDays === 1 ? "Tag" : "Tage"} vor der
            Veranstaltung kostenlos. Zu einer Stornierung macht dieser
            Anbieter keine Angabe — frag im Zweifel vor der Buchung nach.
          </p>
        </Abschnitt>
      )}

      {storno.length > 0 && (
        <Abschnitt
          id="storno"
          titel="Wenn der Termin doch nicht stattfindet"
          untertitel="Die Stornobedingungen dieses Anbieters in Zahlen."
        >
          <div className="overflow-x-auto">
            <table className="w-full min-w-[380px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                  <th scope="col" className="py-2 pr-4 font-semibold">Storniert</th>
                  <th scope="col" className="py-2 text-right font-semibold">Erstattung</th>
                </tr>
              </thead>
              <tbody>
                {[...storno]
                  .sort((a, b) => b.startDays - a.startDays)
                  .map((r) => (
                    <tr key={r.startDays} className="border-b border-slate-100 last:border-0">
                      <th scope="row" className="py-2.5 pr-4 font-medium text-slate-900">
                        {r.endDays >= 999
                          ? `mehr als ${r.startDays} Tage vorher`
                          : `${r.startDays} bis ${r.endDays} Tage vorher`}
                      </th>
                      <td className="py-2.5 text-right font-semibold tabular-nums text-slate-900">
                        {r.refundPercent} %
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4">
            {cancellation?.freeReschedule
              ? `Eine Umbuchung auf einen anderen Termin ist kostenlos, bis ${
                  cancellation.freeRescheduleDays || 0
                } Tage vorher.`
              : "Eine Umbuchung auf einen anderen Termin ist bei diesem Anbieter nicht gesondert geregelt — frag im Zweifel vor der Buchung nach."}
          </p>
        </Abschnitt>
      )}

      {printLayouts?.offersLayouts && (
        <Abschnitt
          id="layouts"
          titel="Das Layout der Ausdrucke"
          untertitel="Was auf dem Streifen steht, den die Gäste mitnehmen."
        >
          <p>
            {printLayouts.layoutCount
              ? `${printLayouts.layoutCount} Vorlagen stehen zur Auswahl`
              : "Es stehen Vorlagen zur Auswahl"}
            {printLayouts.ownLayoutPossible
              ? " — und ihr könnt ein eigenes Layout liefern, etwa mit Namen und Datum oder mit Firmenlogo. Schickt die Vorlage einige Tage vor dem Termin, damit sie eingerichtet werden kann."
              : ". Ein eigenes Layout ist bei diesem Anbieter nicht vorgesehen."}
          </p>
        </Abschnitt>
      )}
    </div>
  );
}
